// Module frontend surface.
//
// The shell discovers this file automatically (modules/*/frontend/index.js) and
// merges what it exports. Adding this module needs no edit to the shell.
//
// Three routes: the portfolio, one place's file, and the map. The map is a
// route of its own rather than a tab, because it is the screen somebody keeps
// open on a second monitor while planning a week.

export const routes = [
  {
    path: '/properties',
    name: 'properties',
    component: () => import('./pages/PropertiesPage.vue'),
    meta: { permission: 'properties:read', title: 'properties.title' },
  },
  // Static before the parameter, for the reader: route ranking already handles it.
  {
    path: '/properties/map',
    name: 'properties-map',
    component: () => import('./pages/PropertyMapPage.vue'),
    meta: { permission: 'properties:read', title: 'properties.map.title' },
  },
  //
  // The form is a screen and not a dialog, like every other form in the ERP.
  // Half of it is a map, and a map in a modal is a postage stamp: putting the
  // point down is the part of this form that needs the room, and it was the
  // part that had the least of it.
  //
  // Behind `properties:write` and not `read`, so the field staff never reaches a
  // form it cannot submit.
  {
    path: '/properties/new',
    name: 'property-new',
    component: () => import('./pages/PropertyFormPage.vue'),
    meta: { permission: 'properties:write', title: 'properties.form.newTitle' },
  },
  {
    path: '/properties/:id/edit',
    name: 'property-edit',
    component: () => import('./pages/PropertyFormPage.vue'),
    meta: { permission: 'properties:write', title: 'properties.form.editTitle' },
  },
  {
    path: '/properties/:id',
    name: 'property',
    component: () => import('./pages/PropertyPage.vue'),
    meta: { permission: 'properties:read', title: 'properties.detailTitle' },
  },
]

/**
 * One entry, and it sits in `business` beside the work.
 *
 * `properties:read` is held by the field staff, so this is a menu entry most of the
 * company sees — which is right: looking up where you are going and what the
 * gate code is is the single most common thing anybody does with this module.
 */
export const navigation = [
  {
    to: '/properties',
    labelKey: 'properties.title',
    permission: 'properties:read',
    icon: 'estate',
    group: 'business',
  },
]

// Strings this module contributes, merged per locale. Four languages from the
// first commit (§16), including what this module is called on the PLATFORM's own
// screens — Roles, Settings, Audit and the assistant panel — because a module
// that makes somebody add keys to the shell is not extractable.
export const messages = {
  es: {
    errors: {
      ZONE_NAME_TAKEN: 'Ya hay una ruta con ese nombre.',
    },
    properties: {
      title: 'Emplazamientos',
      subtitle: 'Dónde se trabaja: la ficha del sitio, su ruta y quién lo lleva',
      detailTitle: 'Emplazamiento',
      notFound: 'No encontramos este emplazamiento',
      backToList: 'Volver a la lista',
      empty: 'Todavía no hay emplazamientos',
      emptyHint: 'Un emplazamiento es el sitio donde vais a trabajar: un jardín, una comunidad, un local.',
      noZone: 'Sin ruta',
      banner: {
        count: '{count} sin visita',
        since: 'en más de {days} días:',
        more: 'y {count} más',
      },
      dashboard: {
        active: 'Emplazamientos activos',
        activeHelp: 'Cuántos sitios estáis llevando ahora mismo, sin contar los parados ni los suspendidos.',
        suspended: 'Suspendidos',
        suspendedHelp: 'Cuántos sitios están parados por decisión de alguien, con su motivo apuntado.',
        noMap: 'Sin punto en el mapa',
        noMapHelp: 'Cuántos sitios no tienen coordenadas. Son los que faltan cuando se planifica una ruta con el mapa.',
        mine: 'Mis emplazamientos',
        mineHelp: 'Los cinco primeros sitios en los que estás en el equipo. Cada quien ve los suyos.',
      },
      actions: { new: 'Nuevo emplazamiento' },
      status: { active: 'Activo', inactive: 'Parado', suspended: 'Suspendido' },
      view: { label: 'Vista', zones: 'Rutas', grid: 'Cuadrícula', map: 'Mapa' },
      stats: {
        active: 'Activos', inactive: 'Parados', suspended: 'Suspendidos', noMap: 'Sin punto',
      },
      filters: {
        search: 'Buscar',
        searchPlaceholder: 'Nombre, código, dirección, población o cliente',
        status: 'Estado',
        anyStatus: 'Cualquier estado',
        serviceType: 'Tipo de sitio',
        anyService: 'Cualquier tipo',
      },
      card: {
        overdue: 'Hace {days} días',
        never: 'Sin visitas',
        noPoint: 'Sin punto en el mapa',
      },
      select: {
        start: 'Seleccionar',
        cancel: 'Cancelar selección',
        chosen: '{count} seleccionados',
        labelName: 'Nombre del grupo',
        noColour: 'Sin color',
        apply: 'Poner etiqueta',
        clear: 'Quitar etiqueta',
        applied: 'Etiqueta puesta',
        cleared: 'Etiqueta quitada',
      },
      zones: {
        rename: 'Renombrar la ruta',
        colour: 'Color de la ruta',
        remove: 'Eliminar la ruta',
        removeTitle: '¿Eliminar la ruta «{name}»?',
        removeBody: 'La ruta desaparece y sus {count} emplazamientos se quedan, sin ruta. No se borra ningún sitio.',
        removed: 'Ruta eliminada. {count} emplazamientos se han quedado sin ruta.',
      },
      form: {
        newTitle: 'Nuevo emplazamiento',
        newSubtitle: 'Un sitio nuevo: dónde está, de quién es y quién lo lleva',
        sectionWhat: 'Qué es y de quién',
        sectionWhere: 'Dónde está',
        sectionRound: 'La ruta y el servicio',
        sectionNotes: 'Lo que hay que saber al llegar',
        sectionWho: 'Quién va',
        team: 'Equipo',
        teamPlaceholder: 'Buscar un equipo',
        lead: 'Responsable',
        leadHint: 'Quien responde de este sitio',
        leadPlaceholder: 'Buscar una persona',
        crew: 'Cuadrilla habitual',
        crewHint: 'Quien va normalmente. Es lo que hace que cada uno vea sus emplazamientos en «Los míos» y en el inicio.',
        crewPlaceholder: 'Añadir a alguien',
        crewRemove: 'Quitar a {who} de la cuadrilla',
        crewFull: 'La cuadrilla está llena ({max} personas)',
        unlink: 'Desvincular del registro',
        editTitle: 'Editar el emplazamiento',
        name: 'Nombre',
        namePlaceholder: 'Como lo llamáis vosotros',
        customer: 'Cliente',
        customerPlaceholder: 'Buscar o escribir el nombre',
        address: 'Dirección',
        city: 'Población',
        postalCode: 'Código postal',
        point: 'Punto en el mapa',
        pointHint: 'Haz clic en el mapa, o arrastra el marcador',
        pointEmpty: 'Haz clic para poner el punto',
        lat: 'Latitud',
        lng: 'Longitud',
        clearPoint: 'Quitar el punto',
        zone: 'Ruta',
        noZone: 'Sin ruta',
        newZone: 'Nueva ruta',
        zoneName: 'Nombre de la ruta',
        createZone: 'Crear y elegir',
        serviceType: 'Tipo de sitio',
        contractType: 'Tipo de contrato',
        areaSqm: 'Superficie (m²)',
        status: 'Estado',
        statusLocked: 'Un sitio suspendido se reactiva desde su ficha, con un motivo.',
        instructions: 'Instrucciones para el equipo',
        instructionsHint: 'Lo que hay que saber antes de bajar de la furgoneta: el código de la puerta, el perro, dónde está la toma de agua.',
        notes: 'Notas',
        internalNotes: 'Notas internas',
        weeklyControl: 'Avisar si pasa tiempo sin visita',
        weeklyControlHint: 'Marca el sitio cuando pasan {days} días sin una visita registrada.',
        keepOpen: 'Seguir añadiendo',
        create: 'Crear',
        created: '«{name}» creado',
        saved: 'Emplazamiento guardado',
      },
      detail: {
        summary: 'Resumen',
        file: 'Ficha',
        instructions: 'Instrucciones para el equipo',
        lastVisit: 'Última visita',
        noVisitData: 'Sin datos',
        neverVisited: 'Nunca se ha visitado',
        noVisitYet: 'Aún sin visitas',
        overdueBy: 'Hace {days} días',
        statusReason: 'Motivo: {reason}',
        crew: 'Equipo habitual',
        photos: 'Fotos',
        history: 'Historial',
        noPoint: 'Este sitio no tiene punto en el mapa',
        addPoint: 'Ponerle el punto',
        changeStatus: 'Cambiar el estado',
        reason: 'Motivo',
        reasonHint: 'Dentro de seis meses alguien preguntará por qué, y no estará quien lo decidió.',
        statusChanged: 'Estado cambiado',
        archive: 'Archivar',
        restore: 'Devolver a las listas',
        reportIncident: 'Avisar de una incidencia',
        restored: 'Emplazamiento devuelto a las listas',
        isArchived: 'Este emplazamiento está archivado: no sale en ninguna lista ni se puede planificar trabajo en él. Devuélvelo para volver a usarlo.',
        archiveTitle: '¿Archivar «{name}»?',
        archiveBody: 'Sale de las listas y de los buscadores. No se borra: las órdenes y las facturas que lo nombran siguen apuntando aquí.',
        archived: 'Emplazamiento archivado',
      },
      activity: {
        title: 'Actividad',
      },
      history: {
        created: 'Dado de alta',
        updated: 'Ficha modificada',
        status: 'Estado cambiado',
        note: 'Nota',
        visit: 'Visita registrada',
      },
      // What the assistant's card says (backend/assistant.js, add_property_note).
      assistant: {
        note: {
          action: { instruction: 'Añadir una instrucción para la cuadrilla', note: 'Añadir una nota' },
          summary: { instruction: 'Instrucción para {property}: {text}', note: 'Nota en {property}: {text}' },
          field: { text: 'Dice', kind: 'Para', property: 'Emplazamiento' },
          kind: { instruction: 'La cuadrilla', note: 'La oficina' },
          where: 'Emplazamiento',
          after: 'Se añade al final, con la fecha de hoy; lo que había se queda',
          notes: { noProperty: '¿Qué emplazamiento? Elígelo antes de confirmar' },
          outcome: 'Nota añadida en {property}',
        },
      },
      map: {
        title: 'Mapa de emplazamientos',
        subtitle: '{pinned} en el mapa · {unpinned} sin punto',
        search: 'Buscar en la lista',
        pinned: 'En el mapa ({count})',
        unpinned: 'Sin punto ({count})',
        addPoint: 'Falta el punto',
      },
    },
    // ── Lo que este módulo se llama en las pantallas de la plataforma ─────────
    modules: { properties: 'Emplazamientos' },
    assistant: {
      openers: {
        properties: {
          'what-in-zone': '¿Qué tenemos en una ruta?',
          'how-many': '¿Cuántos sitios llevamos?',
        },
      },
    },
    permissions: {
      properties: {
        read: 'Ver los emplazamientos y sus fichas',
        write: 'Dar de alta emplazamientos y editar sus fichas',
        manage: 'Rutas, etiquetas y archivar un emplazamiento',
      },
    },
    settings: {
      ns: {
        properties: {
          title: 'Emplazamientos',
          subtitle: 'Cómo llamáis a lo que lleváis, y cuándo avisar de que hace tiempo que nadie va',
          serviceTypes: {
            label: 'Los tipos de sitio que lleváis',
            help: 'Vuestros, no nuestros. Al renombrar uno, los sitios que ya lo tenían siguen mostrando el nombre viejo.',
          },
          contractTypes: {
            label: 'Los tipos de contrato que firmáis',
            help: 'Solo si tenéis más de uno. Déjalo vacío y los sitios no llevan tipo de contrato.',
          },
          visitWindowDays: {
            label: 'Días sin visita antes de avisar',
            help: 'Solo se aplica a los sitios con el aviso activado. Siete para una ruta semanal, catorce para una quincenal.',
          },
        },
      },
    },
    audit: {
      actions: {
        properties: {
          property_created: 'Emplazamiento creado',
          property_updated: 'Emplazamiento modificado',
          note_added: 'Nota añadida',
          property_active: 'Emplazamiento reactivado',
          property_inactive: 'Emplazamiento parado',
          property_suspended: 'Emplazamiento suspendido',
          property_archived: 'Emplazamiento archivado',
          property_restored: 'Emplazamiento recuperado',
          property_labelled: 'Etiqueta puesta a varios emplazamientos',
          property_unlabelled: 'Etiqueta quitada de varios emplazamientos',
          property_photo_removed: 'Foto eliminada',
          zone_created: 'Ruta creada',
          zone_updated: 'Ruta modificada',
          zone_removed: 'Ruta eliminada',
        },
      },
    },
  },

  ca: {
    errors: {
      ZONE_NAME_TAKEN: 'Ja hi ha una ruta amb aquest nom.',
    },
    properties: {
      title: 'Emplaçaments',
      subtitle: 'On es treballa: la fitxa del lloc, la seva ruta i qui el porta',
      detailTitle: 'Emplaçament',
      notFound: 'No trobem aquest emplaçament',
      backToList: 'Tornar a la llista',
      empty: 'Encara no hi ha emplaçaments',
      emptyHint: 'Un emplaçament és el lloc on aneu a treballar: un jardí, una comunitat, un local.',
      noZone: 'Sense ruta',
      banner: {
        count: '{count} sense visita',
        since: 'en més de {days} dies:',
        more: 'i {count} més',
      },
      dashboard: {
        active: 'Emplaçaments actius',
        activeHelp: 'Quants llocs porteu ara mateix, sense comptar els aturats ni els suspesos.',
        suspended: 'Suspesos',
        suspendedHelp: 'Quants llocs estan aturats per decisió d\'algú, amb el motiu apuntat.',
        noMap: 'Sense punt al mapa',
        noMapHelp: 'Quants llocs no tenen coordenades. Són els que falten quan es planifica una ruta amb el mapa.',
        mine: 'Els meus emplaçaments',
        mineHelp: 'Els cinc primers llocs on ets a la colla. Cadascú veu els seus.',
      },
      actions: { new: 'Nou emplaçament' },
      status: { active: 'Actiu', inactive: 'Aturat', suspended: 'Suspès' },
      view: { label: 'Vista', zones: 'Rutes', grid: 'Graella', map: 'Mapa' },
      stats: {
        active: 'Actius', inactive: 'Aturats', suspended: 'Suspesos', noMap: 'Sense punt',
      },
      filters: {
        search: 'Cercar',
        searchPlaceholder: 'Nom, codi, adreça, població o client',
        status: 'Estat',
        anyStatus: 'Qualsevol estat',
        serviceType: 'Tipus de lloc',
        anyService: 'Qualsevol tipus',
      },
      card: {
        overdue: 'Fa {days} dies',
        never: 'Sense visites',
        noPoint: 'Sense punt al mapa',
      },
      select: {
        start: 'Seleccionar',
        cancel: 'Cancel·lar la selecció',
        chosen: '{count} seleccionats',
        labelName: 'Nom del grup',
        noColour: 'Sense color',
        apply: 'Posar etiqueta',
        clear: 'Treure etiqueta',
        applied: 'Etiqueta posada',
        cleared: 'Etiqueta treta',
      },
      zones: {
        rename: 'Reanomenar la ruta',
        colour: 'Color de la ruta',
        remove: 'Eliminar la ruta',
        removeTitle: 'Vols eliminar la ruta «{name}»?',
        removeBody: 'La ruta desapareix i els seus {count} emplaçaments es queden, sense ruta. No s\'esborra cap lloc.',
        removed: 'Ruta eliminada. {count} emplaçaments s\'han quedat sense ruta.',
      },
      form: {
        newTitle: 'Nou emplaçament',
        newSubtitle: 'Un lloc nou: on és, de qui és i qui el porta',
        sectionWhat: 'Què és i de qui és',
        sectionWhere: 'On és',
        sectionRound: 'La ruta i el servei',
        sectionNotes: 'El que cal saber en arribar',
        sectionWho: 'Qui hi va',
        team: 'Equip',
        teamPlaceholder: 'Cercar un equip',
        lead: 'Responsable',
        leadHint: 'Qui respon d’aquest lloc',
        leadPlaceholder: 'Cercar una persona',
        crew: 'Colla habitual',
        crewHint: 'Qui hi va normalment. És el que fa que cadascú vegi els seus emplaçaments a «Els meus» i a l’inici.',
        crewPlaceholder: 'Afegir algú',
        crewRemove: 'Treure {who} de la colla',
        crewFull: 'La colla és plena ({max} persones)',
        unlink: 'Desvincular del registre',
        editTitle: 'Editar l\'emplaçament',
        name: 'Nom',
        namePlaceholder: 'Com l\'anomeneu vosaltres',
        customer: 'Client',
        customerPlaceholder: 'Cercar o escriure el nom',
        address: 'Adreça',
        city: 'Població',
        postalCode: 'Codi postal',
        point: 'Punt al mapa',
        pointHint: 'Fes clic al mapa, o arrossega el marcador',
        pointEmpty: 'Fes clic per posar el punt',
        lat: 'Latitud',
        lng: 'Longitud',
        clearPoint: 'Treure el punt',
        zone: 'Ruta',
        noZone: 'Sense ruta',
        newZone: 'Nova ruta',
        zoneName: 'Nom de la ruta',
        createZone: 'Crear i triar',
        serviceType: 'Tipus de lloc',
        contractType: 'Tipus de contracte',
        areaSqm: 'Superfície (m²)',
        status: 'Estat',
        statusLocked: 'Un lloc suspès es reactiva des de la seva fitxa, amb un motiu.',
        instructions: 'Instruccions per a la colla',
        instructionsHint: 'El que cal saber abans de baixar de la furgoneta: el codi de la porta, el gos, on és la presa d\'aigua.',
        notes: 'Notes',
        internalNotes: 'Notes internes',
        weeklyControl: 'Avisar si passa temps sense visita',
        weeklyControlHint: 'Marca el lloc quan passen {days} dies sense una visita registrada.',
        keepOpen: 'Seguir afegint',
        create: 'Crear',
        created: '«{name}» creat',
        saved: 'Emplaçament desat',
      },
      detail: {
        summary: 'Resum',
        file: 'Fitxa',
        instructions: 'Instruccions per a la colla',
        lastVisit: 'Última visita',
        noVisitData: 'Sense dades',
        neverVisited: "No s'hi ha anat mai",
        noVisitYet: 'Encara sense visites',
        overdueBy: 'Fa {days} dies',
        statusReason: 'Motiu: {reason}',
        crew: 'Colla habitual',
        photos: 'Fotos',
        history: 'Historial',
        noPoint: 'Aquest lloc no té punt al mapa',
        addPoint: 'Posar-li el punt',
        changeStatus: 'Canviar l\'estat',
        reason: 'Motiu',
        reasonHint: 'D\'aquí a sis mesos algú preguntarà per què, i qui ho va decidir no hi serà.',
        statusChanged: 'Estat canviat',
        archive: 'Arxivar',
        restore: 'Retornar a les llistes',
        reportIncident: "Avisar d'una incidència",
        restored: 'Emplaçament retornat a les llistes',
        isArchived: 'Aquest emplaçament està arxivat: no surt a cap llista ni s\'hi pot planificar feina. Retorna\'l per tornar-lo a fer servir.',
        archiveTitle: 'Vols arxivar «{name}»?',
        archiveBody: 'Surt de les llistes i dels cercadors. No s\'esborra: les ordres i les factures que l\'anomenen segueixen apuntant aquí.',
        archived: 'Emplaçament arxivat',
      },
      activity: {
        title: 'Activitat',
      },
      history: {
        created: 'Donat d\'alta',
        updated: 'Fitxa modificada',
        status: 'Estat canviat',
        note: 'Nota',
        visit: 'Visita registrada',
      },
      // What the assistant's card says (backend/assistant.js, add_property_note).
      assistant: {
        note: {
          action: { instruction: 'Afegir una instrucció per a la colla', note: 'Afegir una nota' },
          summary: { instruction: 'Instrucció per a {property}: {text}', note: 'Nota a {property}: {text}' },
          field: { text: 'Diu', kind: 'Per a', property: 'Emplaçament' },
          kind: { instruction: 'La colla', note: 'L\'oficina' },
          where: 'Emplaçament',
          after: 'S\'afegeix al final, amb la data d\'avui; el que hi havia es queda',
          notes: { noProperty: 'Quin emplaçament? Tria\'l abans de confirmar' },
          outcome: 'Nota afegida a {property}',
        },
      },
      map: {
        title: 'Mapa d\'emplaçaments',
        subtitle: '{pinned} al mapa · {unpinned} sense punt',
        search: 'Cercar a la llista',
        pinned: 'Al mapa ({count})',
        unpinned: 'Sense punt ({count})',
        addPoint: 'Falta el punt',
      },
    },
    modules: { properties: 'Emplaçaments' },
    assistant: {
      openers: {
        properties: {
          'what-in-zone': 'Què tenim en una ruta?',
          'how-many': 'Quants llocs portem?',
        },
      },
    },
    permissions: {
      properties: {
        read: 'Veure els emplaçaments i les seves fitxes',
        write: 'Donar d\'alta emplaçaments i editar-ne les fitxes',
        manage: 'Rutes, etiquetes i arxivar un emplaçament',
      },
    },
    settings: {
      ns: {
        properties: {
          title: 'Emplaçaments',
          subtitle: 'Com anomeneu el que porteu, i quan avisar que fa temps que no hi va ningú',
          serviceTypes: {
            label: 'Els tipus de lloc que porteu',
            help: 'Vostres, no nostres. En reanomenar-ne un, els llocs que ja el tenien segueixen mostrant el nom antic.',
          },
          contractTypes: {
            label: 'Els tipus de contracte que signeu',
            help: 'Només si en teniu més d\'un. Deixa-ho buit i els llocs no porten tipus de contracte.',
          },
          visitWindowDays: {
            label: 'Dies sense visita abans d\'avisar',
            help: 'Només s\'aplica als llocs amb l\'avís activat. Set per a una ruta setmanal, catorze per a una quinzenal.',
          },
        },
      },
    },
    audit: {
      actions: {
        properties: {
          property_created: 'Emplaçament creat',
          property_updated: 'Emplaçament modificat',
          note_added: 'Nota afegida',
          property_active: 'Emplaçament reactivat',
          property_inactive: 'Emplaçament aturat',
          property_suspended: 'Emplaçament suspès',
          property_archived: 'Emplaçament arxivat',
          property_restored: 'Emplaçament recuperat',
          property_labelled: 'Etiqueta posada a diversos emplaçaments',
          property_unlabelled: 'Etiqueta treta de diversos emplaçaments',
          property_photo_removed: 'Foto eliminada',
          zone_created: 'Ruta creada',
          zone_updated: 'Ruta modificada',
          zone_removed: 'Ruta eliminada',
        },
      },
    },
  },

  en: {
    errors: {
      ZONE_NAME_TAKEN: 'There is already a round with that name.',
    },
    properties: {
      title: 'Properties',
      subtitle: 'Where the work happens: the file of the place, its round and who looks after it',
      detailTitle: 'Property',
      notFound: 'We cannot find this property',
      backToList: 'Back to the list',
      empty: 'No properties yet',
      emptyHint: 'A property is the place you go to work: a garden, a block of flats, a shop.',
      noZone: 'No round',
      banner: {
        count: '{count} not visited',
        since: 'in more than {days} days:',
        more: 'and {count} more',
      },
      dashboard: {
        active: 'Active properties',
        activeHelp: 'How many places you are looking after right now, not counting the paused or suspended ones.',
        suspended: 'Suspended',
        suspendedHelp: 'How many places are stopped because somebody decided so, with the reason recorded.',
        noMap: 'Missing from the map',
        noMapHelp: 'How many places have no coordinates. These are the ones missing when a round is planned on the map.',
        mine: 'My properties',
        mineHelp: 'The first five places you are on the crew of. Everybody sees their own.',
      },
      actions: { new: 'New property' },
      status: { active: 'Active', inactive: 'Paused', suspended: 'Suspended' },
      view: { label: 'View', zones: 'Rounds', grid: 'Grid', map: 'Map' },
      stats: {
        active: 'Active', inactive: 'Paused', suspended: 'Suspended', noMap: 'No point',
      },
      filters: {
        search: 'Search',
        searchPlaceholder: 'Name, code, address, town or customer',
        status: 'Status',
        anyStatus: 'Any status',
        serviceType: 'Kind of place',
        anyService: 'Any kind',
      },
      card: {
        overdue: '{days} days ago',
        never: 'Never visited',
        noPoint: 'Not on the map',
      },
      select: {
        start: 'Select',
        cancel: 'Cancel selection',
        chosen: '{count} selected',
        labelName: 'Group name',
        noColour: 'No colour',
        apply: 'Apply label',
        clear: 'Remove label',
        applied: 'Label applied',
        cleared: 'Label removed',
      },
      zones: {
        rename: 'Rename the round',
        colour: 'Colour of the round',
        remove: 'Delete the round',
        removeTitle: 'Delete the round "{name}"?',
        removeBody: 'The round goes and its {count} properties stay, with no round. No place is deleted.',
        removed: 'Round deleted. {count} properties are now without one.',
      },
      form: {
        newTitle: 'New property',
        newSubtitle: 'A new place: where it is, whose it is and who works it',
        sectionWhat: 'What it is and whose',
        sectionWhere: 'Where it is',
        sectionRound: 'The round and the service',
        sectionNotes: 'What to know on arrival',
        sectionWho: 'Who goes',
        team: 'Team',
        teamPlaceholder: 'Search for a team',
        lead: 'Lead',
        leadHint: 'Who answers for this place',
        leadPlaceholder: 'Search for a person',
        crew: 'Usual crew',
        crewHint: 'Who normally goes. It is what puts a place on somebody’s own list, both here and on the home screen.',
        crewPlaceholder: 'Add somebody',
        crewRemove: 'Take {who} off the crew',
        crewFull: 'The crew is full ({max} people)',
        unlink: 'Unlink from the record',
        editTitle: 'Edit the property',
        name: 'Name',
        namePlaceholder: 'What you call it yourselves',
        customer: 'Customer',
        customerPlaceholder: 'Search, or type the name',
        address: 'Address',
        city: 'Town',
        postalCode: 'Postcode',
        point: 'Point on the map',
        pointHint: 'Click the map, or drag the marker',
        pointEmpty: 'Click to drop the point',
        lat: 'Latitude',
        lng: 'Longitude',
        clearPoint: 'Remove the point',
        zone: 'Round',
        noZone: 'No round',
        newZone: 'New round',
        zoneName: 'Name of the round',
        createZone: 'Create and pick',
        serviceType: 'Kind of place',
        contractType: 'Kind of contract',
        areaSqm: 'Area (m²)',
        status: 'Status',
        statusLocked: 'A suspended property is brought back from its own file, with a reason.',
        instructions: 'Instructions for the crew',
        instructionsHint: 'What to know before getting out of the van: the gate code, the dog, where the tap is.',
        notes: 'Notes',
        internalNotes: 'Internal notes',
        weeklyControl: 'Tell me when nobody has been for a while',
        weeklyControlHint: 'Flags the place when {days} days pass with no visit recorded.',
        keepOpen: 'Keep adding',
        create: 'Create',
        created: '"{name}" created',
        saved: 'Property saved',
      },
      detail: {
        summary: 'Summary',
        file: 'File',
        instructions: 'Instructions for the crew',
        lastVisit: 'Last visit',
        noVisitData: 'No data',
        neverVisited: 'Never visited',
        noVisitYet: 'No visits yet',
        overdueBy: '{days} days ago',
        statusReason: 'Reason: {reason}',
        crew: 'Usual crew',
        photos: 'Photos',
        history: 'History',
        noPoint: 'This place has no point on the map',
        addPoint: 'Put its point on',
        changeStatus: 'Change the status',
        reason: 'Reason',
        reasonHint: 'In six months somebody will ask why, and whoever decided will not be there.',
        statusChanged: 'Status changed',
        archive: 'Archive',
        restore: 'Bring back',
        reportIncident: 'Report an incident',
        restored: 'Property brought back',
        isArchived: 'This property is archived: it is on no list and no work can be planned on it. Bring it back to use it again.',
        archiveTitle: 'Archive "{name}"?',
        archiveBody: 'It leaves the lists and the pickers. It is not deleted: the orders and invoices that name it keep pointing here.',
        archived: 'Property archived',
      },
      activity: {
        title: 'Activity',
      },
      history: {
        created: 'Opened',
        updated: 'File edited',
        status: 'Status changed',
        note: 'Note',
        visit: 'Visit recorded',
      },
      // What the assistant's card says (backend/assistant.js, add_property_note).
      assistant: {
        note: {
          action: { instruction: 'Add an instruction for the crew', note: 'Add a note' },
          summary: { instruction: 'Instruction for {property}: {text}', note: 'Note on {property}: {text}' },
          field: { text: 'Says', kind: 'For', property: 'Property' },
          kind: { instruction: 'The crew', note: 'The office' },
          where: 'Property',
          after: 'Added at the end with today\'s date; what was there stays',
          notes: { noProperty: 'Which property? Pick it before confirming' },
          outcome: 'Note added to {property}',
        },
      },
      map: {
        title: 'Map of properties',
        subtitle: '{pinned} on the map · {unpinned} with no point',
        search: 'Search the list',
        pinned: 'On the map ({count})',
        unpinned: 'No point ({count})',
        addPoint: 'Point missing',
      },
    },
    modules: { properties: 'Properties' },
    assistant: {
      openers: {
        properties: {
          'what-in-zone': 'What have we got on a round?',
          'how-many': 'How many properties do we look after?',
        },
      },
    },
    permissions: {
      properties: {
        read: 'See the properties and their files',
        write: 'Open properties and edit their files',
        manage: 'Rounds, labels and archiving a property',
      },
    },
    settings: {
      ns: {
        properties: {
          title: 'Properties',
          subtitle: 'What you call the places you look after, and when to say nobody has been for a while',
          serviceTypes: {
            label: 'The kinds of place you look after',
            help: 'Yours, not ours. Renaming one leaves the properties already on it showing the old name.',
          },
          contractTypes: {
            label: 'The kinds of contract you sign',
            help: 'Only if you have more than one. Leave it empty and properties do not carry one.',
          },
          visitWindowDays: {
            label: 'Days without a visit before flagging',
            help: 'Only applies to places with the warning switched on. Seven for a weekly round, fourteen for a fortnightly one.',
          },
        },
      },
    },
    audit: {
      actions: {
        properties: {
          property_created: 'Property created',
          property_updated: 'Property edited',
          note_added: 'Note added',
          property_active: 'Property reactivated',
          property_inactive: 'Property paused',
          property_suspended: 'Property suspended',
          property_archived: 'Property archived',
          property_restored: 'Property restored',
          property_labelled: 'Label applied to several properties',
          property_unlabelled: 'Label removed from several properties',
          property_photo_removed: 'Photo removed',
          zone_created: 'Round created',
          zone_updated: 'Round edited',
          zone_removed: 'Round deleted',
        },
      },
    },
  },

  fr: {
    errors: {
      ZONE_NAME_TAKEN: 'Une tournée porte déjà ce nom.',
    },
    properties: {
      title: 'Emplacements',
      subtitle: 'Où le travail se fait : la fiche du lieu, sa tournée et qui s\'en occupe',
      detailTitle: 'Emplacement',
      notFound: 'Nous ne trouvons pas cet emplacement',
      backToList: 'Retour à la liste',
      empty: 'Pas encore d\'emplacements',
      emptyHint: 'Un emplacement est le lieu où vous allez travailler : un jardin, une copropriété, un local.',
      noZone: 'Sans tournée',
      banner: {
        count: '{count} sans visite',
        since: 'depuis plus de {days} jours :',
        more: 'et {count} de plus',
      },
      dashboard: {
        active: 'Emplacements actifs',
        activeHelp: 'Combien de lieux vous suivez en ce moment, sans compter ceux en pause ni les suspendus.',
        suspended: 'Suspendus',
        suspendedHelp: 'Combien de lieux sont arrêtés sur décision de quelqu\'un, avec le motif noté.',
        noMap: 'Absents de la carte',
        noMapHelp: 'Combien de lieux n\'ont pas de coordonnées. Ce sont ceux qui manquent quand on planifie une tournée sur la carte.',
        mine: 'Mes emplacements',
        mineHelp: 'Les cinq premiers lieux où vous êtes dans l\'équipe. Chacun voit les siens.',
      },
      actions: { new: 'Nouvel emplacement' },
      status: { active: 'Actif', inactive: 'En pause', suspended: 'Suspendu' },
      view: { label: 'Vue', zones: 'Tournées', grid: 'Grille', map: 'Carte' },
      stats: {
        active: 'Actifs', inactive: 'En pause', suspended: 'Suspendus', noMap: 'Sans point',
      },
      filters: {
        search: 'Rechercher',
        searchPlaceholder: 'Nom, code, adresse, commune ou client',
        status: 'Statut',
        anyStatus: 'Tous les statuts',
        serviceType: 'Type de lieu',
        anyService: 'Tous les types',
      },
      card: {
        overdue: 'Il y a {days} jours',
        never: 'Jamais visité',
        noPoint: 'Absent de la carte',
      },
      select: {
        start: 'Sélectionner',
        cancel: 'Annuler la sélection',
        chosen: '{count} sélectionnés',
        labelName: 'Nom du groupe',
        noColour: 'Sans couleur',
        apply: 'Poser l\'étiquette',
        clear: 'Retirer l\'étiquette',
        applied: 'Étiquette posée',
        cleared: 'Étiquette retirée',
      },
      zones: {
        rename: 'Renommer la tournée',
        colour: 'Couleur de la tournée',
        remove: 'Supprimer la tournée',
        removeTitle: 'Supprimer la tournée «{name}» ?',
        removeBody: 'La tournée disparaît et ses {count} emplacements restent, sans tournée. Aucun lieu n\'est supprimé.',
        removed: 'Tournée supprimée. {count} emplacements se retrouvent sans tournée.',
      },
      form: {
        newTitle: 'Nouvel emplacement',
        newSubtitle: 'Un nouveau lieu : où il est, à qui il est et qui s’en occupe',
        sectionWhat: 'Ce que c’est et à qui',
        sectionWhere: 'Où il est',
        sectionRound: 'La tournée et le service',
        sectionNotes: 'Ce qu’il faut savoir en arrivant',
        sectionWho: 'Qui y va',
        team: 'Équipe',
        teamPlaceholder: 'Chercher une équipe',
        lead: 'Responsable du lieu',
        leadHint: 'Qui répond de ce lieu',
        leadPlaceholder: 'Chercher une personne',
        crew: 'Équipe habituelle',
        crewHint: 'Qui y va normalement. C’est ce qui fait apparaître le lieu dans la liste de chacun, ici et sur l’accueil.',
        crewPlaceholder: 'Ajouter quelqu’un',
        crewRemove: 'Retirer {who} de l’équipe',
        crewFull: 'L’équipe est complète ({max} personnes)',
        unlink: 'Dissocier de la fiche',
        editTitle: 'Modifier l\'emplacement',
        name: 'Nom',
        namePlaceholder: 'Comme vous l\'appelez',
        customer: 'Client',
        customerPlaceholder: 'Chercher, ou saisir le nom',
        address: 'Adresse',
        city: 'Commune',
        postalCode: 'Code postal',
        point: 'Point sur la carte',
        pointHint: 'Cliquez sur la carte, ou déplacez le marqueur',
        pointEmpty: 'Cliquez pour poser le point',
        lat: 'Latitude',
        lng: 'Longitude',
        clearPoint: 'Retirer le point',
        zone: 'Tournée',
        noZone: 'Sans tournée',
        newZone: 'Nouvelle tournée',
        zoneName: 'Nom de la tournée',
        createZone: 'Créer et choisir',
        serviceType: 'Type de lieu',
        contractType: 'Type de contrat',
        areaSqm: 'Surface (m²)',
        status: 'Statut',
        statusLocked: 'Un emplacement suspendu est réactivé depuis sa fiche, avec un motif.',
        instructions: 'Consignes pour l\'équipe',
        instructionsHint: 'Ce qu\'il faut savoir avant de descendre du camion : le code du portail, le chien, où est le robinet.',
        notes: 'Notes',
        internalNotes: 'Notes internes',
        weeklyControl: 'Prévenir si personne n\'y va depuis un moment',
        weeklyControlHint: 'Signale le lieu quand {days} jours passent sans visite enregistrée.',
        keepOpen: 'Continuer à ajouter',
        create: 'Créer',
        created: '«{name}» créé',
        saved: 'Emplacement enregistré',
      },
      detail: {
        summary: 'Résumé',
        file: 'Fiche',
        instructions: 'Consignes pour l\'équipe',
        lastVisit: 'Dernière visite',
        noVisitData: 'Pas de données',
        neverVisited: 'Jamais visité',
        noVisitYet: 'Pas encore de visite',
        overdueBy: 'Il y a {days} jours',
        statusReason: 'Motif : {reason}',
        crew: 'Équipe habituelle',
        photos: 'Photos',
        history: 'Historique',
        noPoint: 'Ce lieu n\'a pas de point sur la carte',
        addPoint: 'Lui poser son point',
        changeStatus: 'Changer le statut',
        reason: 'Motif',
        reasonHint: 'Dans six mois quelqu\'un demandera pourquoi, et celui qui a décidé ne sera plus là.',
        statusChanged: 'Statut changé',
        archive: 'Archiver',
        restore: 'Remettre dans les listes',
        reportIncident: 'Signaler un incident',
        restored: 'Emplacement remis dans les listes',
        isArchived: 'Cet emplacement est archivé : il ne figure sur aucune liste et aucun travail ne peut y être planifié. Remettez-le pour le réutiliser.',
        archiveTitle: 'Archiver «{name}» ?',
        archiveBody: 'Il sort des listes et des sélecteurs. Il n\'est pas supprimé : les ordres et les factures qui le nomment continuent de pointer ici.',
        archived: 'Emplacement archivé',
      },
      activity: {
        title: 'Activité',
      },
      history: {
        created: 'Ouvert',
        updated: 'Fiche modifiée',
        status: 'Statut changé',
        note: 'Note',
        visit: 'Visite enregistrée',
      },
      // What the assistant's card says (backend/assistant.js, add_property_note).
      assistant: {
        note: {
          action: { instruction: 'Ajouter une consigne pour l\'équipe', note: 'Ajouter une note' },
          summary: { instruction: 'Consigne pour {property} : {text}', note: 'Note sur {property} : {text}' },
          field: { text: 'Dit', kind: 'Pour', property: 'Emplacement' },
          kind: { instruction: 'L\'équipe', note: 'Le bureau' },
          where: 'Emplacement',
          after: 'Ajoutée à la fin avec la date du jour ; ce qui était écrit reste',
          notes: { noProperty: 'Quel emplacement ? Choisissez-le avant de confirmer' },
          outcome: 'Note ajoutée à {property}',
        },
      },
      map: {
        title: 'Carte des emplacements',
        subtitle: '{pinned} sur la carte · {unpinned} sans point',
        search: 'Chercher dans la liste',
        pinned: 'Sur la carte ({count})',
        unpinned: 'Sans point ({count})',
        addPoint: 'Point manquant',
      },
    },
    modules: { properties: 'Emplacements' },
    assistant: {
      openers: {
        properties: {
          'what-in-zone': 'Qu\'avons-nous sur une tournée ?',
          'how-many': 'Combien d\'emplacements suivons-nous ?',
        },
      },
    },
    permissions: {
      properties: {
        read: 'Voir les emplacements et leurs fiches',
        write: 'Ouvrir des emplacements et modifier leurs fiches',
        manage: 'Tournées, étiquettes et archivage d\'un emplacement',
      },
    },
    settings: {
      ns: {
        properties: {
          title: 'Emplacements',
          subtitle: 'Comment vous appelez ce que vous suivez, et quand signaler que personne n\'y va depuis un moment',
          serviceTypes: {
            label: 'Les types de lieu que vous suivez',
            help: 'Les vôtres, pas les nôtres. En renommer un laisse les emplacements qui l\'avaient déjà avec l\'ancien nom.',
          },
          contractTypes: {
            label: 'Les types de contrat que vous signez',
            help: 'Seulement si vous en avez plus d\'un. Laissez vide et les emplacements n\'en portent pas.',
          },
          visitWindowDays: {
            label: 'Jours sans visite avant de signaler',
            help: 'Ne s\'applique qu\'aux lieux dont l\'alerte est activée. Sept pour une tournée hebdomadaire, quatorze pour une quinzaine.',
          },
        },
      },
    },
    audit: {
      actions: {
        properties: {
          property_created: 'Emplacement créé',
          property_updated: 'Emplacement modifié',
          note_added: 'Note ajoutée',
          property_active: 'Emplacement réactivé',
          property_inactive: 'Emplacement mis en pause',
          property_suspended: 'Emplacement suspendu',
          property_archived: 'Emplacement archivé',
          property_restored: 'Emplacement restauré',
          property_labelled: 'Étiquette posée sur plusieurs emplacements',
          property_unlabelled: 'Étiquette retirée de plusieurs emplacements',
          property_photo_removed: 'Photo supprimée',
          zone_created: 'Tournée créée',
          zone_updated: 'Tournée modifiée',
          zone_removed: 'Tournée supprimée',
        },
      },
    },
  },
}

// What the assistant panel says while this module's tools run: another
// contract with the backend (backend/assistant.js), in its own file.
import { withToolLabels } from './i18n/toolLabels.js'

Object.assign(messages, withToolLabels(messages))
